/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useSingleFileUpload.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing single file upload (drag & drop, validation, preview).
    Simpler version than useFileUpload for MOH (single file only).
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useRef } from 'react';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

/**
 * Hook for single file upload
 */
export function useSingleFileUpload() {
  const { showError } = useNotifications();
  const { t } = useI18n();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(
    (file: File) => {
      if (!file.type.startsWith('audio/')) {
        showError(t('recordingManagement.errors.invalidFileType'));
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        showError(t('recordingManagement.errors.fileTooLarge'));
        return;
      }

      setSelectedFile(file);

      // Create preview URL
      const url = URL.createObjectURL(file);
      setFilePreview(url);
    },
    [showError, t],
  );

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFileSelect(e.dataTransfer.files[0]);
      }
    },
    [handleFileSelect],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
        handleFileSelect(e.target.files[0]);
      }
    },
    [handleFileSelect],
  );

  const removeFile = useCallback(() => {
    setSelectedFile(null);
    if (filePreview) {
      URL.revokeObjectURL(filePreview);
    }
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [filePreview]);

  return {
    selectedFile,
    filePreview,
    dragActive,
    fileInputRef,
    handleFileSelect,
    handleDrag,
    handleDrop,
    handleFileInputChange,
    removeFile,
  };
}

